import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Alert, AlertTitle, Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Fragment, useEffect, useMemo, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useParams } from 'react-router-dom'

import { SummaryValidation } from '@components/prereg_validation/summary_validation'
import { useBookingContext } from '@providers/booking/booking_context'
import { useLocationContext } from '@providers/location/location_provider'
import { isUserLoggedIn } from '@providers/session/helpers'
import { useSessionContext } from '@providers/session/session_context'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { i18WithDefault as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'

import { FlexCardDisplay } from 'components-ui/src/flex_card_display/flex_card_display'
import { InvoiceCard } from 'components-ui/src/purchase_summary/invoice_card/invoice_card'
import { getPoliciesData } from 'components-ui/src/purchase_summary/purchase_receipt/helper'
import { PurchaseReceipt } from 'components-ui/src/purchase_summary/purchase_receipt/purchase_receipt'
import { PurchaseReceiptSkeleton } from 'components-ui/src/purchase_summary/purchase_receipt/purchase_receipt_skeleton'
import { SurchargePolicy } from 'components-ui/src/purchase_summary/surcharge_policy/surcharge_policy'
import { allowNoPayments } from 'components-ui/src/shared/location'
import { getOrderPrimaryServiceTypeName, isVisitOrder } from 'components-ui/src/shared/orders'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useClientBookingConfigs } from 'service-api/src/hooks/use_client_booking_configs'
import { useClientOrder } from 'service-api/src/hooks/use_client_order'
import { useClientReceipt } from 'service-api/src/hooks/use_client_receipt'
import { OrderClientDto } from 'service-api/src/types/api'
import { PurchaseSummarySkeleton } from '../purchase_summary/purchase_summary_skeleton'

const getPurchaseSummaryTitle = (serviceTypeName?: string) => {
  switch (serviceTypeName) {
    case 'grooming':
      return 'paymentSummary.title.grooming'
    case 'packages':
      return 'paymentSummary.title.packages'
    default:
      return 'paymentSummary.title'
  }
}

export const BookingConfirmation = () => {
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const { accountName = '', orderId = '' } = useParams()
  const theme = useTheme()
  const { location } = useLocationContext()
  const renderStyle = useMediaQuery(theme.breakpoints.down('md')) ? 'mobile' : 'desktop'
  const [order, setOrder] = useState<OrderClientDto | undefined>()
  const isMobile = renderStyle === 'mobile'
  const serviceTypeName = getOrderPrimaryServiceTypeName(order) || ''
  const isGrooming = serviceTypeName === 'grooming'
  const isVisit = isVisitOrder(order)

  useEffect(() => {
    document.title = t('title.bookingConfirmation')
  }, [])

  const {
    data: receipt,
    isSuccess: receiptIsSuccess,
    isLoading: receiptIsLoading,
    refetch: receiptRefetch,
  } = useClientReceipt(accountName, orderId)
  const {
    data: orderDto,
    isSuccess: orderIsSuccess,
    isLoading: orderIsLoading,
    error: orderError,
    refetch: orderRefetch,
  } = useClientOrder(accountName, orderId)
  const bookingConfs = useClientBookingConfigs(accountName, order?.primaryServiceType?.serviceType?.name || '', {
    enabled: isVisit,
  })
  const hideRefundPolicy = !isVisit || allowNoPayments(order?.primaryServiceType?.locationBookingConf?.[0])
  const policyCardsData = useMemo(
    () => (receiptIsSuccess ? getPoliciesData(receipt, hideRefundPolicy) : []),
    [receiptIsSuccess, receipt, hideRefundPolicy]
  )

  const loggedIn = isUserLoggedIn(sessionCtx.state.user)

  useEffect(() => {
    if (!orderIsSuccess) {
      return
    }

    if (!loggedIn) {
      setTimeout(
        () =>
          navigateRoute(
            'signIn',
            {},
            {
              state: {
                redirect: {
                  route: 'paymentSummary',
                  params: { orderId },
                },
                email: orderDto.userData?.[0]?.email,
                userExists: !!(orderDto.ownerId || orderDto.owner?.id),
              },
            }
          ),
        0
      )
      return
    }

    setOrder(orderDto)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDto, orderIsSuccess, loggedIn, sessionCtx.state.user?.email])

  useEffect(() => {
    if (orderError) {
      setTimeout(() => navigateRoute('notFound', {}), 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderError])

  useEffect(() => {
    bookingCtx.dispatch({
      type: 'SET_SEARCH_STATE',
      payload: { value: 'paymentSummary' },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.currentState])

  const firePurchaseEvent = (order: OrderClientDto) => {
    const items =
      order.invoices?.flatMap((invoice, index) =>
        invoice.items?.map((invoiceItem) => ({
          item_id: invoiceItem.offer?.id || invoiceItem.offerId || '',
          item_name: invoiceItem.displayName || '',
          affiliation: location?.displayName || '',
          index,
          item_category: invoice.locationServiceType?.serviceType?.name || '',
          item_category2: invoiceItem.locationSpecies?.species?.name || '',
          item_category3: invoiceItem.offerType,
          price: invoiceItem.subtotal || 0,
          quantity: invoiceItem.qty || 0,
        }))
      ) || []

    const purchaseData = {
      transaction_id: order.id || '',
      value: order.invoices?.reduce((sum, invoice) => sum + (invoice.total || 0), 0) || 0,
      tax: order.invoices?.reduce((sum, invoice) => sum + (invoice.tax || 0), 0) || 0,
      currency: order.payments?.[0].currency || '',
      items,
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'purchase',
        ecommerce: purchaseData,
      },
    })
  }

  // Fire purchase event when order is successfully fetched
  useEffect(() => {
    if (orderDto && orderIsSuccess && location && bookingCtx.state.shouldFireGAPurchaseEvent) {
      firePurchaseEvent(orderDto)

      bookingCtx.dispatch({
        type: 'SET_FIRE_PURCHASE_EVENT',
        payload: { value: false },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDto, orderIsSuccess, location])

  if (orderIsLoading || receiptIsLoading || bookingConfs.isFetching) {
    return <PurchaseSummarySkeleton />
  }

  if (!order?.invoices) {
    return null
  }

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto', mb: 4 }}>
      <Spacer height={3} />
      <Stack alignItems={'center'}>
        <CheckCircleIcon fontSize="large" sx={{ color: theme.palette.success.light, mb: 1 }} />
        <Typography variant="h1">{t(getPurchaseSummaryTitle(serviceTypeName))}</Typography>
      </Stack>

      <Spacer height={3} />
      {isVisit && <SummaryValidation orderId={order.id || ''} />}
      <Spacer height={3} />

      <Stack direction="column" spacing={3} flex={1} width="100%">
        {order.invoices.map((invoice) => (
          <InvoiceCard
            key={invoice.id}
            invoice={invoice}
            order={order}
            onCancelInvoice={() => {
              orderRefetch()
              receiptRefetch()
            }}
          />
        ))}
      </Stack>

      {/* {isIntakeEnabled(bookingConfs.data?.locationBookingConf) && (
        <>
          <Spacer height={3} />
          <Intake
            flexBookingConfs={bookingConfs.data?.flexBookingConfs || []}
            order={order || {}}
            onSave={(newOrder) => {
              setOrder(newOrder)
            }}
          />
        </>
      )} */}

      <Spacer height={3} />

      <Typography variant="h2">{t('paymentSummary.subtitle')}</Typography>
      <Spacer height={2} />
      <Stack direction={{ sx: 'column', md: 'row' }} alignItems="start" spacing={3} flex={1}>
        <Stack spacing={3} sx={{ flex: 1 }} width="100%">
          <Box>
            {isGrooming && (
              <Alert severity="info">
                <AlertTitle>{t('paymentForm.groomRequestRefund')}</AlertTitle>
                {t('paymentForm.groomRequestNotice')}
              </Alert>
            )}
            {policyCardsData.map((group, index) => (
              <Fragment key={index}>
                <FlexCardDisplay cardData={group} />
                <Spacer height={2} />
              </Fragment>
            ))}
          </Box>
          <Box>
            {bookingConfs.isSuccess && (
              <SurchargePolicy order={order} bookingConfig={bookingConfs.data} receipt={receipt} />
            )}
          </Box>
        </Stack>
        <Stack direction="column" spacing={2} flex={1} mt={isMobile ? 3 : 0} width="100%">
          <Box sx={{ flex: 1 }}>
            {receiptIsLoading || orderIsLoading ? (
              <PurchaseReceiptSkeleton />
            ) : (
              <PurchaseReceipt receipt={receipt} isVisit={isVisit} order={order} />
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  )
}
